import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {
  IconChevronLeft, IconChevronRight, IconExternalLink, IconMinus, IconPlus,
  IconTrash, IconUserPlus, IconUsers, IconX,
} from '@tabler/icons-react'
import {apiDelete, apiGet} from './api/client'
import {parseDate} from './api/gramps'
import {useApiToken} from './api/useApiToken'
import {
  attachMedia, detachMedia, findGroupTag, loadMediaMeta, looksVideo, mediaDate, mediaYear,
  saveMediaMeta, setGroup, type MediaMeta,
} from './api/media'
import {ageInYear, birthYear, shortName, yearByAge, YEARS_RU} from './api/tree'
import type {TreeIndex} from './api/types'
import Overlay from './Overlay'
import PersonPicker from './PersonPicker'

export interface ViewItem {
  handle: string
  gramps_id?: string
  desc?: string
  mime?: string
  date?: {dateval?: number[]}
  tag_list?: string[]
}

interface Props {
  ix: TreeIndex
  /** что листаем — в том же порядке, что на экране */
  handles: string[]
  index: number
  onIndex: (i: number) => void
  onClose: () => void
  /** данные изменились: привязка, дата, удаление */
  onChanged: () => void
  onOpenPerson: (h: string) => void
}

/**
 * Просмотр снимка во весь экран со всем, что к нему прилагается.
 *
 * Отдельным компонентом, а не куском галереи: то же самое нужно из
 * карточки человека, а копия такого размера разошлась бы с оригиналом
 * на первой же правке.
 *
 * Сам снимок компонент дочитывает по handle: у вызывающего может быть
 * только список ссылок (как в карточке человека), а показывать надо
 * подпись, дату и метки.
 */
export default function MediaViewer({
  ix, handles, index, onIndex, onClose, onChanged, onOpenPerson,
}: Props) {
  const jwt = useApiToken()
  const [item, setItem] = useState<ViewItem | null>(null)
  const [groupTag, setGroupTag] = useState<string | null>(null)
  const [err, setErr] = useState('')
  const [busy, setBusy] = useState(false)
  /** окно «о снимке»: люди, дата, описание */
  const [editing, setEditing] = useState(false)
  const [meta, setMeta] = useState<MediaMeta | null>(null)
  const [ageWho, setAgeWho] = useState('')
  const [age, setAge] = useState('')
  const [asking, setAsking] = useState(false)

  const handle = handles[index]

  useEffect(() => { findGroupTag().then(setGroupTag).catch(() => {}) }, [])

  useEffect(() => {
    let alive = true
    setItem(null)
    if (!handle) return
    apiGet<ViewItem>(`/media/${handle}`)
      .then(m => alive && setItem(m))
      .catch(() => alive && setItem({handle}))
    return () => { alive = false }
  }, [handle])

  /* Кто на снимке — обратной картой из людей: у медиа обратных ссылок
     в списке нет, а отдельный запрос на каждый — это сотня запросов. */
  const who = useMemo(() => {
    const out: string[] = []
    for (const p of ix.people.values()) {
      if ((p.media_list || []).some(r => r.ref === handle)) out.push(p.handle)
    }
    return out
  }, [ix, handle])

  /* Кого считаем по возрасту. Окно часто открывают на снимке, где ещё
     никого нет: выбор пуст, человека отмечают уже здесь. Поэтому берём
     выбранное, а если такого в списке нет — первого из отмеченных. */
  const whoAge = who.includes(ageWho) ? ageWho : who[0] || ''
  const typedYear = meta ? parseDate(meta.date)?.[2] || 0 : 0
  const isVideoItem = looksVideo(item?.mime)
  const isGroup = !!groupTag && (item?.tag_list || []).includes(groupTag)

  const step = useCallback((d: number) => {
    if (!handles.length) return
    onIndex((index + d + handles.length) % handles.length)
  }, [handles.length, index, onIndex])

  /* ---------- увеличение ----------
   * Колесо тянет к курсору, а не к центру: на групповом фото увеличивают
   * конкретное лицо. Перетаскивание — только когда увеличено, иначе оно
   * мешало бы закрывать просмотр щелчком по фону. */
  const stageRef = useRef<HTMLDivElement>(null)
  const [zoom, setZoom] = useState({k: 1, x: 0, y: 0})
  const dragged = useRef(false)

  useEffect(() => setZoom({k: 1, x: 0, y: 0}), [handle])

  useEffect(() => {
    const el = stageRef.current
    if (!el) return
    const clamp = (k: number) => Math.min(8, Math.max(1, k))
    const zoomAt = (cx: number, cy: number, factor: number) => {
      const r = el.getBoundingClientRect()
      const px = cx - (r.left + r.width / 2)
      const py = cy - (r.top + r.height / 2)
      setZoom(z => {
        const k = clamp(z.k * factor)
        if (k === z.k) return z
        const s = k / z.k
        return k === 1 ? {k: 1, x: 0, y: 0} : {k, x: px - (px - z.x) * s, y: py - (py - z.y) * s}
      })
    }
    const onWheel = (e: WheelEvent) => {
      e.preventDefault()
      zoomAt(e.clientX, e.clientY, e.deltaY < 0 ? 1.15 : 1 / 1.15)
    }
    let from: {x: number; y: number; ox: number; oy: number} | null = null
    const down = (e: PointerEvent) => {
      dragged.current = false
      setZoom(z => {
        if (z.k > 1) {
          from = {x: e.clientX, y: e.clientY, ox: z.x, oy: z.y}
          el.setPointerCapture(e.pointerId)
        }
        return z
      })
    }
    const move = (e: PointerEvent) => {
      if (!from) return
      const dx = e.clientX - from.x
      const dy = e.clientY - from.y
      if (Math.abs(dx) > 3 || Math.abs(dy) > 3) dragged.current = true
      setZoom(z => ({...z, x: from!.ox + dx, y: from!.oy + dy}))
    }
    const up = () => { from = null }
    el.addEventListener('wheel', onWheel, {passive: false})
    el.addEventListener('pointerdown', down)
    el.addEventListener('pointermove', move)
    el.addEventListener('pointerup', up)
    el.addEventListener('pointercancel', up)
    ;(el as unknown as {__zoom: typeof zoomAt}).__zoom = zoomAt
    return () => {
      el.removeEventListener('wheel', onWheel)
      el.removeEventListener('pointerdown', down)
      el.removeEventListener('pointermove', move)
      el.removeEventListener('pointerup', up)
      el.removeEventListener('pointercancel', up)
    }
  }, [handle, isVideoItem])

  const zoomBy = (factor: number) => {
    const r = stageRef.current?.getBoundingClientRect()
    const el = stageRef.current as unknown as {__zoom?: (x: number, y: number, f: number) => void}
    if (r) el?.__zoom?.(r.left + r.width / 2, r.top + r.height / 2, factor)
  }

  /* Клавиши просмотра работают, только когда поверх ничего не открыто:
     иначе стрелки в поле описания заодно листали бы фотографии. */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (editing || asking) {
        if (e.key === 'Escape') { e.stopPropagation(); setEditing(false); setAsking(false) }
        return
      }
      const el = e.target as HTMLElement | null
      if (el && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName))) return
      if (e.key === 'Escape') onClose()
      if (e.key === 'ArrowLeft') step(-1)
      if (e.key === 'ArrowRight') step(1)
      if (e.key === '+' || e.key === '=') zoomBy(1.4)
      if (e.key === '-') zoomBy(1 / 1.4)
      if (e.key === '0') setZoom({k: 1, x: 0, y: 0})
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [editing, asking, step, onClose])

  async function openEdit() {
    setErr('')
    setEditing(true)
    setMeta(null)
    setAge('')
    setAgeWho(who[0] || '')
    try {
      setMeta(await loadMediaMeta(handle))
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Не удалось прочитать снимок')
    }
  }

  const setAgeFor = (person: string, value: string) => {
    setAgeWho(person)
    setAge(value)
    const y = yearByAge(ix.people.get(person), Number(value))
    if (value.trim() && y) setMeta(m => (m ? {...m, date: String(y)} : m))
  }

  async function saveEdit() {
    if (meta?.date && !parseDate(meta.date)) { setErr('Дату не разобрать'); return }
    setBusy(true)
    setErr('')
    try {
      if (meta) await saveMediaMeta(handle, meta)
      setItem(await apiGet<ViewItem>(`/media/${handle}`))
      setEditing(false)
      setMeta(null)
      onChanged()
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Не удалось сохранить')
    } finally {
      setBusy(false)
    }
  }

  async function link(person: string, on: boolean) {
    setBusy(true)
    setErr('')
    try {
      if (on) await attachMedia(person, handle)
      else await detachMedia(person, handle)
      onChanged()
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Не удалось изменить привязку')
    } finally {
      setBusy(false)
    }
  }

  async function markGroup(on: boolean) {
    setBusy(true)
    setErr('')
    try {
      await setGroup(handle, on)
      setItem(await apiGet<ViewItem>(`/media/${handle}`))
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Не удалось изменить метку')
    } finally {
      setBusy(false)
    }
  }

  async function remove() {
    setBusy(true)
    setErr('')
    try {
      await apiDelete(`/media/${handle}`)
      setAsking(false)
      onChanged()
      onClose()
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Не удалось удалить')
      setBusy(false)
    }
  }

  if (!handle) return null

  return (
    <>
      <Overlay><div className="viewer" onClick={() => { if (!dragged.current) onClose() }}>
        <button className="vclose" title="Закрыть (Esc)" onClick={onClose}>
          <IconX size={20} stroke={1.8} />
        </button>
        {handles.length > 1 && (
          <>
            <button className="vnav left" title="Предыдущий (←)"
                    onClick={e => { e.stopPropagation(); step(-1) }}>
              <IconChevronLeft size={26} stroke={1.8} />
            </button>
            <button className="vnav right" title="Следующий (→)"
                    onClick={e => { e.stopPropagation(); step(1) }}>
              <IconChevronRight size={26} stroke={1.8} />
            </button>
          </>
        )}

        {isVideoItem ? (
          <video className="vshot" onClick={e => e.stopPropagation()} controls autoPlay
                 src={`/api/media/${handle}/file?jwt=${jwt}`} />
        ) : (
          <div className="vstage" ref={stageRef} onClick={e => e.stopPropagation()}>
            {/* Увеличенный снимок просим крупнее: на 1400 точек лица
                дальних рядов на групповом фото уже не разобрать. */}
            <img className={'vshot' + (zoom.k > 1 ? ' zoomed' : '')}
                 style={{transform: `translate(${zoom.x}px, ${zoom.y}px) scale(${zoom.k})`}}
                 src={`/api/media/${handle}/thumbnail/${zoom.k > 1 ? 2600 : 1400}?jwt=${jwt}`}
                 alt="" draggable={false} />
          </div>
        )}

        <div className="vbar" onClick={e => e.stopPropagation()}>
          <div className="vname" title={item?.desc || undefined}>
            {item?.desc || item?.gramps_id || ''}
            {item && mediaDate(item) && <em className="vyear">{mediaDate(item)}</em>}
          </div>
          <div className="vrow">
            <div className="vwho">
              {who.length ? who.map(h => {
                const a = item ? ageInYear(ix.people.get(h), mediaYear(item)) : null
                return (
                  <button type="button" key={h} className="chip on" onClick={() => onOpenPerson(h)}>
                    {shortName(ix.people.get(h))}
                    {a !== null && <em className="chipage">{a}&nbsp;{YEARS_RU(a)}</em>}
                  </button>
                )
              }) : <span className="muted">никто не отмечен</span>}
            </div>
            <div className="spacer" />
            {!isVideoItem && (
              <span className="vzoom">
                <button className="btn" title="Мельче (−)" onClick={() => zoomBy(1 / 1.4)}>
                  <IconMinus size={15} stroke={2} />
                </button>
                <button className="btn zval" title="Обычный размер (0)"
                        onClick={() => setZoom({k: 1, x: 0, y: 0})}>
                  {Math.round(zoom.k * 100)}%
                </button>
                <button className="btn" title="Крупнее (+)" onClick={() => zoomBy(1.4)}>
                  <IconPlus size={15} stroke={2} />
                </button>
              </span>
            )}
            <span className="muted num">{index + 1} / {handles.length}</span>
            <button className="btn" onClick={openEdit}>
              <IconUserPlus size={15} stroke={1.7} /> Кто, когда, что
            </button>
            {/* Общий снимок, где опознан один человек, иначе попадает
                в «Портреты» и теряется среди них. */}
            <button className={'btn' + (isGroup ? ' on' : '')}
                    disabled={busy || who.length > 1}
                    title={who.length > 1
                      ? 'Здесь и так отмечено несколько человек'
                      : 'Пометить как групповой снимок'}
                    onClick={() => markGroup(!isGroup)}>
              <IconUsers size={15} stroke={1.7} /> Групповой
            </button>
            <a className="btn" href={`/api/media/${handle}/file?jwt=${jwt}`}
               target="_blank" rel="noreferrer">
              <IconExternalLink size={15} stroke={1.7} /> Оригинал
            </a>
            <button className="btn warn" onClick={() => setAsking(true)}>
              <IconTrash size={15} stroke={1.7} /> Удалить
            </button>
          </div>
        </div>
      </div></Overlay>

      {/* ---------- о снимке: кто, когда, что ---------- */}
      {editing && (
        <Overlay><div className="modal-bg" onClick={() => { setEditing(false); setMeta(null) }}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="mhead">
              <h2>О снимке</h2>
              <button type="button" className="close" aria-label="Закрыть"
                      onClick={() => { setEditing(false); setMeta(null) }}>
                <IconX size={18} stroke={1.8} />
              </button>
            </div>

            <img className="linkshot" src={`/api/media/${handle}/thumbnail/480?jwt=${jwt}`} alt="" />

            {meta && (
              <>
                <label>
                  Когда снято
                  <input value={meta.date} placeholder="1979, или 05.1979, или 14.05.1979"
                         className={meta.date && !parseDate(meta.date) ? 'bad' : ''}
                         onChange={e => setMeta({...meta, date: e.target.value})} />
                </label>

                {/* Год по возрасту: у семейных фотографий чаще помнят
                    «здесь ему лет пять», чем год съёмки. */}
                {who.length > 0 && (
                  <>
                    <div className="two">
                      <label>
                        Кто
                        <select className="sel" value={whoAge}
                                onChange={e => setAgeFor(e.target.value, age)}>
                          {who.map(h => (
                            <option key={h} value={h}>{shortName(ix.people.get(h))}</option>
                          ))}
                        </select>
                      </label>
                      <label>
                        Сколько лет
                        <input value={age} inputMode="numeric" placeholder="например, 34"
                               onChange={e => setAgeFor(whoAge, e.target.value)} />
                      </label>
                    </div>
                    {age.trim() && !yearByAge(ix.people.get(whoAge), Number(age)) && (
                      <span className="hint">
                        {birthYear(ix.people.get(whoAge))
                          ? 'Возраст не похож на правду'
                          : 'У этого человека не заполнен год рождения — считать не от чего'}
                      </span>
                    )}
                    {typedYear > 0 && (
                      <div className="agelist">
                        {who.map(h => {
                          const a = ageInYear(ix.people.get(h), typedYear)
                          return (
                            <span key={h} className="agerow">
                              <b>{shortName(ix.people.get(h))}</b>
                              {a === null ? <em>год рождения неизвестен</em> : <em>{a} {YEARS_RU(a)}</em>}
                            </span>
                          )
                        })}
                      </div>
                    )}
                  </>
                )}

                <label>
                  Описание
                  <textarea rows={3} value={meta.desc}
                            placeholder="Что за снимок, где и по какому случаю"
                            onChange={e => setMeta({...meta, desc: e.target.value})} />
                </label>
              </>
            )}

            {who.length > 0 && (
              <div className="chosen">
                {who.map(h => (
                  <button type="button" key={h} className="chip on" disabled={busy}
                          title="Отвязать" onClick={() => link(h, false)}>
                    {shortName(ix.people.get(h))} <IconX size={11} stroke={2.2} />
                  </button>
                ))}
              </div>
            )}

            <label>
              Добавить человека
              <PersonPicker ix={ix} value={null} exclude={who}
                            placeholder="начните вводить имя"
                            onChange={h => h && link(h, true)} />
            </label>

            <span className="hint">
              На групповом снимке отмечайте всех по очереди — окно не закрывается.
              Дата и описание сохранятся по кнопке «Готово».
            </span>

            {err && <div className="err inline">{err}</div>}

            <div className="actions">
              <button type="button" className="btn primary" disabled={busy} onClick={saveEdit}>
                {busy ? 'Сохраняем…' : 'Готово'}
              </button>
            </div>
          </div>
        </div></Overlay>
      )}

      {/* ---------- удаление ---------- */}
      {asking && (
        <Overlay><div className="modal-bg" onClick={() => setAsking(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="mhead">
              <h2>Удалить снимок?</h2>
              <button type="button" className="close" onClick={() => setAsking(false)}
                      aria-label="Закрыть">
                <IconX size={18} stroke={1.8} />
              </button>
            </div>
            <img className="linkshot" src={`/api/media/${handle}/thumbnail/480?jwt=${jwt}`} alt="" />
            <div className="mnote">
              Снимок исчезнет из архива насовсем
              {who.length > 0 && ` и отвяжется от ${who.length} чел.`}
            </div>
            {err && <div className="err inline">{err}</div>}
            <div className="actions">
              <button type="button" className="btn" onClick={() => setAsking(false)}>Отмена</button>
              <button type="button" className="btn warn" disabled={busy} onClick={remove}>
                {busy ? 'Удаляем…' : 'Удалить'}
              </button>
            </div>
          </div>
        </div></Overlay>
      )}
    </>
  )
}
